import type { ParticipantsDropdownValue } from '@lmn/common-search-widget/components/ParticipantsDropdown/services/types';
import type { PeopleData } from '@/app/components/search/utils';

/**
 * Adapts a `PeopleData` array to a `ParticipantsDropdownValue` object.
 *
 * This function serves as an adapter between the application's `PeopleData` model
 * and the Common Search Widget's `ParticipantsDropdownValue` model. It converts
 * the room and participant information from `PeopleData` to the format expected
 * by the Common Search Widget.
 *
 * @param peopleData - An array of room data, where each room contains information
 *                     about adults and children.
 * @returns A `ParticipantsDropdownValue` object compatible with the Common Search Widget.
 *
 * @remarks
 * - Each room in the input `peopleData` is converted to a room in the output.
 * - The `adults` count is directly mapped to `adultsCount`.
 * - Children's ages are converted to an array of objects with an `age` property.
 *
 * @example
 * const peopleData = [
 *   { adults: 2, ages: [5, 7] },
 *   { adults: 1, ages: [] }
 * ];
 * const participantsValue = getCSWParticipants(peopleData);
 * // Returns: {
 * //   rooms: [
 * //     { adultsCount: 2, children: [{ age: 5 }, { age: 7 }] },
 * //     { adultsCount: 1, children: [] }
 * //   ]
 * // }
 */

const getCSWParticipants = (peopleData: PeopleData): ParticipantsDropdownValue | undefined => {
  if (!peopleData) return undefined;
  return {
    rooms: peopleData.map((room) => ({
      adultsCount: room.adults,
      children: room.ages.map((age) => ({ age })),
    })),
  };
};

export default getCSWParticipants;
